/* eslint-disable import/first */
import * as React from 'react'
import { UseMutationResult } from 'react-query'
import { FacebookIcon, FacebookShareButton, TwitterIcon, TwitterShareButton } from 'react-share'

import { Button } from '../../../main/components/button'
import { CopyToClipboard } from '../../../main/components/copy-to-clipboard'
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
  useDialogState,
} from '../../../main/components/dialog'
import { ErrorView } from '../../../main/components/error-view'
import { Loading } from '../../../main/components/loading'
import { Heading } from '../../../main/components/typography'

interface ShareResponse {
  imgUrl: string
  shareUrl: string
}

interface ShareProps<ResponseType> {
  state: UseMutationResult<ResponseType | undefined, any, any>
  onShareClick: () => void
  className?: string
}

export function Share<ResponseType extends ShareResponse>(props: ShareProps<ResponseType>) {
  const dialogState = useDialogState()

  return (
    <>
      <Button
        as={DialogTrigger}
        state={dialogState}
        leftContent="share"
        theme="transparent"
        className={props.className}
        onClick={props.onShareClick}
      >
        Share map
      </Button>

      <Dialog state={dialogState} aria-label="Share map">
        <DialogHeader onCloseClick={dialogState.hide}>Share map</DialogHeader>
        <DialogBody className="flex w-145 max-w-full flex-col">
          <ShareDialogBody isError={props.state.isError} isLoading={props.state.isLoading} data={props.state.data} />
        </DialogBody>
        <DialogFooter>
          <Button onClick={dialogState.hide}>Close</Button>
        </DialogFooter>
      </Dialog>
    </>
  )
}

interface ShareDialogBodyProps<ResponseType> {
  isError: boolean
  isLoading: boolean
  data?: ResponseType
}

function ShareDialogBody<ResponseType extends ShareResponse>(props: ShareDialogBodyProps<ResponseType>) {
  if (props.isLoading) {
    return (
      <div className="flex h-110 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (props.isError || !props.data) {
    return (
      <div className="h-110">
        <ErrorView
          showRefresh={false}
          title="Map share failed"
          message={
            <>
              There’s been an error sharing the map.
              <br />
              Please close the dialog and try again.
            </>
          }
        />
      </div>
    )
  }

  return (
    <>
      <img alt={document.title} src={props.data.imgUrl} className="w-full" />
      <div className="mt-4">
        <CopyToClipboard
          label={
            <Heading level={5} className="m-0">
              Copy and paste link in email, website, or forum
            </Heading>
          }
          text={props.data.shareUrl}
          bottomLeftContent={
            <>
              <FacebookShareButton url={props.data.shareUrl}>
                <Button as="span" size="small" leftContent={<FacebookIcon size={16} round={true} className="mr-1" />}>
                  Share
                </Button>
              </FacebookShareButton>
              <TwitterShareButton url={props.data.shareUrl} via="FINVIZ_com" title={document.title} className="ml-1">
                <Button as="span" size="small" leftContent={<TwitterIcon size={16} round={true} className="mr-1" />}>
                  Twitter
                </Button>
              </TwitterShareButton>
            </>
          }
        />
      </div>
    </>
  )
}
